import { useState, useEffect, useCallback, useRef } from "react";
import { useSocketContext } from "../context/SocketProvider";
import useRetryFetch from "./useRetryFetch";
import {
  type ProactiveInsight,
  listProactiveInsights,
  getUnreadInsightCount,
  markInsightSeen as apiMarkSeen,
  dismissInsight as apiDismiss,
} from "../api/proactiveInsights";

// ─── Types ───────────────────────────────────────────────────────────────────

interface UseProactiveInsightsReturn {
  insights: ProactiveInsight[];
  currentFloatingInsight: ProactiveInsight | null;
  unreadCount: number;
  isLoading: boolean;
  hasInsights: boolean;
  markSeen: (id: string) => void;
  dismiss: (id: string) => void;
  fetchInsights: () => Promise<void>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useProactiveInsights(enabled: boolean = true): UseProactiveInsightsReturn {
  const [insights, setInsights] = useState<ProactiveInsight[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [floatingQueue, setFloatingQueue] = useState<ProactiveInsight[]>([]);
  const { socket } = useSocketContext();
  const retryFetch = useRetryFetch();
  const retryFetchRef = useRef(retryFetch);
  const seenTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetchedRef = useRef(false);

  // Keep ref in sync without triggering re-renders
  retryFetchRef.current = retryFetch;

  // Fetch insights from API (stable — uses ref)
  const fetchInsights = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listProactiveInsights(retryFetchRef.current, 50);
      setInsights(data.insights || []);

      // Build floating queue from unseen insights
      const unseen = (data.insights || []).filter(
        (i) => i.status === "generated" || i.status === "delivered"
      );
      setFloatingQueue(unseen);
    } catch (error) {
      console.error("[ProactiveInsights] Failed to fetch:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch unread count (stable — uses ref)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await getUnreadInsightCount(retryFetchRef.current);
      setUnreadCount(data.count);
    } catch (error) {
      console.error("[ProactiveInsights] Failed to fetch unread count:", error);
    }
  }, []);

  // Mark insight as seen
  const markSeen = useCallback(async (id: string) => {
    try {
      await apiMarkSeen(retryFetchRef.current, id);
      setInsights((prev) =>
        prev.map((i) =>
          i._id === id ? { ...i, status: "seen" as const } : i
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("[ProactiveInsights] Failed to mark seen:", error);
    }
  }, []);

  // Dismiss insight — advance the floating queue
  const dismiss = useCallback(async (id: string) => {
    try {
      await apiDismiss(retryFetchRef.current, id);
      setInsights((prev) =>
        prev.map((i) =>
          i._id === id ? { ...i, status: "dismissed" as const } : i
        )
      );
      setFloatingQueue((prev) => prev.filter((i) => i._id !== id));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("[ProactiveInsights] Failed to dismiss:", error);
    }
  }, []);

  // Current floating insight — first in queue
  const currentFloatingInsight = floatingQueue[0] || null;

  // Auto-mark "seen" after 3 seconds of visibility
  useEffect(() => {
    if (seenTimerRef.current) {
      clearTimeout(seenTimerRef.current);
    }

    if (currentFloatingInsight && currentFloatingInsight.status !== "seen") {
      seenTimerRef.current = setTimeout(() => {
        markSeen(currentFloatingInsight._id);
      }, 3000);
    }

    return () => {
      if (seenTimerRef.current) {
        clearTimeout(seenTimerRef.current);
      }
    };
  }, [currentFloatingInsight?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket.IO listener for real-time insights
  useEffect(() => {
    if (!enabled || !socket) return;

    const handleNewInsight = (insight: ProactiveInsight) => {
      setInsights((prev) => [insight, ...prev]);
      setFloatingQueue((prev) => [...prev, insight]);
      setUnreadCount((prev) => prev + 1);
    };

    socket.on("proactive-insight:new", handleNewInsight);

    return () => {
      socket.off("proactive-insight:new", handleNewInsight);
    };
  }, [socket, enabled]);

  // Initial fetch — ONCE when enabled
  useEffect(() => {
    if (!enabled || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchInsights();
    fetchUnreadCount();
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    insights,
    currentFloatingInsight,
    unreadCount,
    isLoading,
    hasInsights: insights.length > 0,
    markSeen,
    dismiss,
    fetchInsights,
  };
}
